'use client'
import { useCallback, useEffect, useRef } from 'react'
import { useDebouncedCallback } from 'ui/hooks'
import type { CreateInvoiceOrderDTO, OrderDTO } from 'ecosystem'
import { useCheckoutContext } from '../../../hooks'

/**
 * Push carrier-supplied overrides to the DRAFT invoice order, debounced.
 *
 * Carrier widgets emit many events while the shopper is filling in addresses
 * and picking delivery options; we don't want to PUT on every keystroke.
 * 350 ms strikes a balance between snappiness and noise — adjust if needed.
 *
 * Returns a `flush` callback that synchronously runs any pending debounced
 * call and resolves once the resulting `updateInvoiceDraft` promise settles.
 * Submit handlers must await it before POSTing — otherwise the submit
 * endpoint GETs a stale DRAFT and PUTs back without the latest FE state
 * (e.g. shippingCost the FE just computed locally).
 */
export const useDebouncedDraftSync = (
  overrides: Partial<CreateInvoiceOrderDTO>,
  enabled: boolean,
  delayMs = 350
) => {
  const { updateInvoiceDraft } = useCheckoutContext()
  const pendingRef = useRef<Promise<OrderDTO | null> | null>(null)

  const sync = useDebouncedCallback((next: Partial<CreateInvoiceOrderDTO>) => {
    pendingRef.current = updateInvoiceDraft(next)
  }, delayMs)

  const serialised = JSON.stringify(overrides)

  useEffect(() => {
    if (!enabled) return
    sync(overrides)
    // `serialised` is the change signal; `overrides` is intentionally excluded
    // to avoid running on every fresh object identity from the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overrides identity changes every render; serialised is the value-equality signal
  }, [serialised, enabled, sync])

  // Flush any pending call on unmount so we don't drop the last edit.
  useEffect(() => () => sync.flush?.(), [sync])

  const flush = useCallback(async () => {
    sync.flush?.()
    if (pendingRef.current) await pendingRef.current
  }, [sync])

  return { flush }
}
